import { authManager } from '../authManager';
import API_CONFIG from '../../config/api';
import { buildApiUrl } from '../../utils/apiUrl';

// BEST PRACTICE: Utiliser l'utilitaire centralisé pour la construction d'URLs

const GET_REQUEST_CACHE_TTL_MS = 30_000;
const getResponseCache = new Map<string, { expiresAt: number; data: any }>();
const inFlightGetRequests = new Map<string, Promise<any>>();

// Types pour l'API
export interface CloudProvider {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
}

export interface CloudConnection {
  id: number;
  provider: CloudProvider | number; // Peut être un objet ou un ID
  provider_detail?: CloudProvider; // Détails du provider (retourné par l'API)
  account_email: string;
  account_name: string;
  account_id: string;
  is_active: boolean;
  last_sync: string | null;
  sync_folders?: string[]; // Liste des IDs de dossiers à synchroniser (pour Google Drive)
  created_at: string;
  updated_at: string;
}

export interface CloudFile {
  id: number;
  connection: CloudConnection;
  cloud_id: string;
  name: string;
  path: string;
  file_type: string;
  file_size: number;
  is_synced: boolean;
  sync_status: string;
  created_at: string;
  updated_at: string;
}

export interface SyncTask {
  id: number;
  connection: CloudConnection;
  task_type: string;
  status: string;
  description: string;
  progress: number;
  total_items: number;
  processed_items: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface SyncLog {
  level: string;
  message: string;
  details: any;
  timestamp: string;
}

// Classe de base pour les services API
export abstract class BaseApiService {
  protected baseUrl: string;

  constructor(baseUrl?: string) {
    // BEST PRACTICE: Utiliser l'utilitaire centralisé si baseUrl n'est pas fourni
    this.baseUrl = baseUrl || API_CONFIG.BASE_URL;
  }

  // Méthodes d'authentification - SIMULÉES
  setToken(token: string) {
    authManager.setTokens(token, authManager.getAccessToken() || '');
  }

  clearToken() {
    authManager.clearAuth();
  }

  protected getHeaders(skipAuth: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (!skipAuth) {
      const token = authManager.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  protected async request<T>(endpoint: string, options: RequestInit = {}, skipAuth: boolean = false): Promise<T> {
    // BEST PRACTICE: Utiliser l'utilitaire centralisé pour construire l'URL
    // Cela garantit une construction cohérente et évite les erreurs
    const url = buildApiUrl(endpoint);
    const headers = this.getHeaders(skipAuth);
    const method = (options.method || 'GET').toUpperCase();
    const accessToken = authManager.getAccessToken() || '';
    const tokenFragment = accessToken ? accessToken.slice(-16) : 'anon';
    const cacheKey = `${method}:${url}:${tokenFragment}`;

    if (method === 'GET') {
      const now = Date.now();
      const cached = getResponseCache.get(cacheKey);
      if (cached && cached.expiresAt > now) {
        return cached.data as T;
      }

      const pending = inFlightGetRequests.get(cacheKey);
      if (pending) {
        return pending as Promise<T>;
      }
    } else {
      // Invalidation simple des GETs après mutation
      getResponseCache.clear();
    }

    const executeRequest = async (): Promise<T> => {
      try {
        // Ajouter un timeout pour éviter que les requêtes pendent indéfiniment
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 secondes
        
        const response = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            ...options.headers,
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          // Gérer les erreurs 401 (non autorisé)
          if (response.status === 401) {
            
            // Ne pas essayer de rafraîchir le token si on est déjà sur la page de login
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
              // Essayer de rafraîchir le token si disponible
              const refreshToken = authManager.getRefreshToken();
              if (refreshToken && !skipAuth) {
                // Note: La logique de refresh est dans authService
                // Pour éviter une dépendance circulaire, on redirige juste
              }
              
              authManager.clearAuth();
              window.location.href = '/login';
            }
            throw new Error('Session expirée');
          }

          // Signaler un problème de disponibilité réseau/serveur côté UI globale
          if ([502, 503, 504].includes(response.status)) {
            this.notifyConnectivityIssue('Connexion instable: le serveur est temporairement indisponible.');
          }

          // Gérer les autres erreurs
          const errorData = await response.json().catch(() => ({}));
          
          // Extraire le message d'erreur le plus pertinent
          let errorMessage = `HTTP error! status: ${response.status}`;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          } else if (typeof errorData === 'object') {
            // Erreurs de validation (ex: {"email": ["This field is required."]})
            const firstError = Object.values(errorData)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = firstError[0];
            }
          }
          
          // Créer un objet d'erreur enrichi pour faciliter la gestion côté frontend
          const error = new Error(errorMessage) as any;
          error.response = {
            status: response.status,
            data: errorData
          };
          throw error;
        }

        // Vérifier si la réponse a du contenu avant d'essayer de parser le JSON
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        
        // Si pas de contenu ou content-length = 0, retourner un objet vide
        if (!contentType || !contentType.includes('application/json') || contentLength === '0') {
          return {} as T;
        }
        
        // Essayer de parser le JSON, mais gérer les cas où il n'y a pas de contenu
        try {
          const data = await response.json();
          return data as T;
        } catch (jsonError) {
          // Si le parsing JSON échoue mais que la réponse est OK, retourner un objet vide
          if (response.ok) {
            return {} as T;
          }
          throw jsonError;
        }
      } catch (error: any) {
        
        // Gérer les erreurs de timeout
        if (error.name === 'AbortError' || error.message.includes('aborted')) {
          this.notifyConnectivityIssue('Connexion internet lente: le chargement prend trop de temps.');
          throw new Error('Le serveur met trop de temps à répondre. Veuillez réessayer.');
        }
        
        // Si c'est une erreur réseau, essayer de donner plus de détails
        if (error.message && error.message.includes('Failed to fetch')) {
          this.notifyConnectivityIssue('Connexion internet instable: impossible de contacter le serveur.');
          throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
        }
        
        throw error;
      }
    };

    if (method === 'GET') {
      const requestPromise = executeRequest()
        .then((data) => {
          getResponseCache.set(cacheKey, {
            expiresAt: Date.now() + GET_REQUEST_CACHE_TTL_MS,
            data,
          });
          inFlightGetRequests.delete(cacheKey);
          return data;
        })
        .catch((error) => {
          inFlightGetRequests.delete(cacheKey);
          throw error;
        });

      inFlightGetRequests.set(cacheKey, requestPromise);
      return requestPromise;
    }

    return executeRequest();
  }

  private notifyConnectivityIssue(message: string): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('api:connectivity-issue', {
        detail: { message },
      })
    );
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    return authManager.isAuthenticated();
  }

  // Déconnexion
  logout(): void {
    this.clearToken();
  }
}
